import React from "react";
import { EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CATEGORY_BADGES } from "@/lib/message-classification";
import { useLocale } from "@/hooks/useLocale";
import { tFormat } from "@/lib/i18n/t-format";
import type { Translations } from "@/lib/i18n/types";

export { useHiddenMessages } from "@/hooks/useHiddenMessages";
export { MessageCard } from "./MessageCard";
export type { MessageCardProps } from "./MessageCard";
export { classifyMessage, CATEGORY_BADGES } from "@/lib/message-classification";

export async function reclassifyMessage(msgId: string, newCategory: string, t: Translations) {
  const { data: existing } = await supabase
    .from("adventure_messages")
    .select("metadata")
    .eq("id", msgId)
    .single();

  const currentMeta = (existing?.metadata as Record<string, any>) || {};
  const { error } = await supabase
    .from("adventure_messages")
    .update({ metadata: { ...currentMeta, override_category: newCategory } })
    .eq("id", msgId);

  if (error) {
    toast.error(t.play_reclassify_fail);
  } else {
    const label = CATEGORY_BADGES[newCategory]?.label || newCategory;
    toast.success(tFormat(t.play_reclassify_success, { label }));
  }
  return !error;
}

function HiddenGroup({ count, onReveal }: { count: number; onReveal: () => void }) {
  const { t } = useLocale();
  return (
    <button
      onClick={onReveal}
      className="w-full flex items-center gap-2 py-1 px-3 text-xs text-muted-foreground hover:text-foreground transition-colors group"
    >
      <div className="flex-1 h-px bg-border" />
      <span className="flex items-center gap-1 shrink-0">
        <EyeOff className="w-3 h-3" />
        {tFormat(t.play_hidden_messages, { count: String(count) })}
      </span>
      <div className="flex-1 h-px bg-border" />
    </button>
  );
}

interface MessageListProps {
  messages: any[];
  hiddenIds: Set<string>;
  onToggleHide: (msgId: string) => void;
  renderMessage: (msg: any) => React.ReactNode;
}

export function MessageListWithHiding({ messages, hiddenIds, onToggleHide, renderMessage }: MessageListProps) {
  const grouped = groupConsecutiveMessages(messages);

  const elements: React.ReactNode[] = [];
  let i = 0;
  while (i < grouped.length) {
    const item = grouped[i];
    const msg = item.message;
    if (hiddenIds.has(msg.id)) {
      let count = 0;
      const hiddenGroup: string[] = [];
      while (i < grouped.length && hiddenIds.has(grouped[i].message.id)) {
        hiddenGroup.push(grouped[i].message.id);
        count++;
        i++;
      }
      elements.push(
        <HiddenGroup
          key={`hidden-${hiddenGroup[0]}`}
          count={count}
          onReveal={() => hiddenGroup.forEach(onToggleHide)}
        />,
      );
    } else {
      elements.push(renderMessage(msg));
      i++;
    }
  }
  return <>{elements}</>;
}


function groupConsecutiveMessages(messages: any[]): Array<{ type: "single"; message: any }> {
  const result: Array<{ type: "single"; message: any }> = [];
  for (const msg of messages) {
    const last = result[result.length - 1];
    if (last) {
      const prevContent = (last.message.content || "").trim();
      const curContent = (msg.content || "").trim();
      // Only skip true duplicates: same content AND same role
      if (prevContent && curContent && prevContent === curContent && last.message.role === msg.role) continue;
    }
    result.push({ type: "single", message: msg });
  }
  return result;
}
